/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Autocomplete,
  TextField,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useLang } from '~/hooks/useLang'
import { authFetch } from '~/services/fetch'
import notify from '~/utils/notify'

interface AgencyProps {
  readonly id: number
  readonly name: string
  readonly acronym: string
}

interface DispatchGroupProps {
  readonly id: number
  readonly name: string
  readonly acronym: string
}

interface AgencyDispatchGroups {
  readonly agency: AgencyProps
  readonly dispatchGroups: readonly DispatchGroupProps[]
  readonly selectedGroup: number | null
  readonly loading: boolean
}

interface EntrySuggestDispatchGroupModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly typeId: number | null
  readonly subtypeId: number | null
  readonly entryId: number
  readonly onDispatchGroupSelected: () => void
  readonly suggestedData?: any
}

export default function EntrySuggestDispatchGroupModal({
  open,
  onClose,
  typeId,
  subtypeId,
  entryId,
  onDispatchGroupSelected,
  suggestedData,
}: EntrySuggestDispatchGroupModalProps) {
  const { translate } = useLang()
  const [agenciesData, setAgenciesData] = useState<
    readonly AgencyDispatchGroups[]
  >([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open && typeId) {
      // Se já tem dados de sugestão, usa eles ao invés de recarregar
      if (suggestedData) {
        const initializedData: readonly AgencyDispatchGroups[] = (suggestedData || []).map(
          (item: any) => ({
            agency: item.agency,
            dispatchGroups: [],
            selectedGroup: null,
            loading: false,
          })
        )
        setAgenciesData(initializedData)
        initializedData.forEach((agencyData) => {
          if (agencyData.agency?.id) {
            loadAgencyDispatchGroups(agencyData.agency.id)
          }
        })
      } else {
        loadSuggestedAgencies()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, typeId, subtypeId, suggestedData])

  const loadSuggestedAgencies = async () => {
    setLoading(true)
    try {
      const { data } = await authFetch({
        url: '/entry/suggested-agencies',
        method: 'POST',
        data: {
          type_id: typeId,
          subtype_id: subtypeId,
        },
      })

      const initializedData: readonly AgencyDispatchGroups[] = (data || []).map(
        (item: any) => ({
          agency: item.agency,
          dispatchGroups: [],
          selectedGroup: null,
          loading: false,
        })
      )

      setAgenciesData(initializedData)

      initializedData.forEach((agencyData) => {
        if (agencyData.agency?.id) {
          loadAgencyDispatchGroups(agencyData.agency.id)
        }
      })
    } catch (error) {
      console.error('Erro ao carregar agências sugeridas:', error)
      notify({
        message: translate('Error loading suggested agencies'),
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const loadAgencyDispatchGroups = async (agencyId: number) => {
    setAgenciesData((prev) =>
      prev.map((item) =>
        item.agency.id === agencyId ? { ...item, loading: true } : item
      )
    )

    try {
      const { data } = await authFetch({
        url: `/agency/${agencyId}/get-dispatch-groups`,
        method: 'GET',
      })

      setAgenciesData((prev) =>
        prev.map((item) =>
          item.agency.id === agencyId
            ? { ...item, dispatchGroups: data || [], loading: false }
            : item
        )
      )
    } catch (error) {
      console.error(`Erro ao carregar grupos da agência ${agencyId}:`, error)
      setAgenciesData((prev) =>
        prev.map((item) =>
          item.agency.id === agencyId ? { ...item, loading: false } : item
        )
      )
    }
  }

  const handleSelectGroupForAgency = (
    agencyId: number,
    selectedGroupId: number | null
  ) => {
    setAgenciesData((prev) =>
      prev.map((item) =>
        item.agency.id === agencyId
          ? { ...item, selectedGroup: selectedGroupId }
          : item
      )
    )
  }

  const handleSaveDispatchGroups = async () => {
    const agenciesWithGroups = agenciesData.filter(
      (item) => item.dispatchGroups.length > 0
    )
    const agenciesWithoutSelection = agenciesWithGroups.filter(
      (item) => item.selectedGroup === null
    )

    if (agenciesWithoutSelection.length > 0) {
      notify({
        message: translate(
          'Please select a dispatch group for all agencies'
        ),
        type: 'warning',
      })
      return
    }

    const allSelectedGroupIds = agenciesData
      .filter((item) => item.selectedGroup !== null)
      .map((item) => item.selectedGroup as number)

    setSubmitting(true)
    try {
      await authFetch({
        url: `/entry/set-multiples-dispatch-group`,
        method: 'POST',
        data: {
          entry_id: entryId,
          dispatch_group_ids: allSelectedGroupIds,
        },
      })

      notify({
        message: translate(
          'Entry saved and dispatch groups added successfully'
        ),
        type: 'success',
      })

      onDispatchGroupSelected()
      handleClose()
    } catch (error) {
      console.error('Erro ao adicionar grupos de despacho:', error)
      notify({
        message: translate('Error adding dispatch groups'),
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setAgenciesData([])
    onClose()
  }

  const getTotalSelectedGroups = () => {
    return agenciesData.filter((item) => item.selectedGroup !== null).length
  }

  const getTotalRequiredGroups = () => {
    return agenciesData.filter((item) => item.dispatchGroups.length > 0).length
  }

  const clickOutModalClose = (
    _event: unknown,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick' && !submitting) {
      handleClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={clickOutModalClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={submitting}
    >
      <DialogTitle>{translate('Add Dispatch Group')}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2, mt: 1, alignItems: 'center' }}>
          {translate(
            'This entry has no location (latitude/longitude). Please select dispatch groups to continue.'
          )}
        </Alert>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : agenciesData.length > 0 ? (
          <>
            <Typography
              variant="subtitle2"
              gutterBottom
              fontWeight="bold"
              sx={{ mb: 2 }}
            >
              {translate('Suggested agencies based on entry type/subtype:')}
            </Typography>

            <Grid container spacing={2}>
              {agenciesData.map((agencyData) => (
                <Grid item xs={12} sm={6} key={agencyData.agency.id}>
                  <FormControl fullWidth size="small">
                    {agencyData.loading ? (
                      <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Autocomplete
                        options={agencyData.dispatchGroups}
                        getOptionLabel={(option) => option.name}
                        value={agencyData.dispatchGroups.find(
                          (group) => group.id === agencyData.selectedGroup
                        ) || null}
                        onChange={(_, newValue) => {
                          handleSelectGroupForAgency(
                            agencyData.agency.id,
                            newValue?.id || null
                          )
                        }}
                        disabled={submitting || agencyData.dispatchGroups.length === 0}
                        noOptionsText={translate('No options')}
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`[${agencyData.agency.acronym}] ${agencyData.agency.name}`}
                            placeholder={translate('Select an option')}
                          />
                        )}
                      />
                    )}
                  </FormControl>
                  {!agencyData.loading &&
                    agencyData.dispatchGroups.length === 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        {translate('No dispatch groups available')}
                      </Typography>
                    )}
                </Grid>
              ))}
            </Grid>

            {getTotalSelectedGroups() > 0 && (
              <Alert severity={getTotalSelectedGroups() === getTotalRequiredGroups() ? 'success' : 'warning'} sx={{ mt: 2 }}>
                {getTotalSelectedGroups()} / {getTotalRequiredGroups()} {translate('dispatch group(s) selected')}
              </Alert>
            )}
          </>
        ) : (
          <Alert severity="info">
            {translate('No agencies found for this entry type/subtype')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={submitting}>
          {translate('Cancel')}
        </Button>
        <LoadingButton
          loading={submitting}
          variant="contained"
          onClick={handleSaveDispatchGroups}
          disabled={getTotalSelectedGroups() !== getTotalRequiredGroups()}
        >
          {translate('Save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
